import React from 'react';
import { EmailTemplate } from '../types';

interface ResetPasswordEmailData {
  name?: string;
  email: string;
  resetLink: string;
  expiresIn?: string;
  ipAddress?: string;
  userAgent?: string;
}

export const ResetPasswordEmailTemplate: EmailTemplate<ResetPasswordEmailData> = {
  id: 'reset-password',
  name: 'Password Reset Email',
  subject: 'Reset your password',
  preview: 'Reset your password for your account',
  body: {
    html: (data) => `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Reset Password</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              background-color: #dc3545;
              color: white;
              padding: 30px;
              text-align: center;
              border-radius: 8px 8px 0 0;
            }
            .content {
              background-color: #ffffff;
              padding: 40px 30px;
              border: 1px solid #e2e8f0;
              border-radius: 0 0 8px 8px;
            }
            .button {
              display: inline-block;
              padding: 14px 28px;
              background-color: #dc3545;
              color: white;
              text-decoration: none;
              border-radius: 6px;
              font-weight: 500;
              margin: 20px 0;
            }
            .security-notice {
              background-color: #f0f9ff;
              border-left: 4px solid #3182ce;
              padding: 12px;
              margin: 20px 0;
              font-size: 14px;
            }
            .details {
              background-color: #f7fafc;
              padding: 15px;
              border-radius: 6px;
              margin: 20px 0;
              font-size: 14px;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              color: #718096;
              font-size: 14px;
            }
            code {
              background-color: #f7fafc;
              padding: 2px 6px;
              border-radius: 3px;
              font-family: monospace;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Password Reset Request</h1>
          </div>
          <div class="content">
            <p>Hi${data.name ? ` ${data.name}` : ''},</p>
            
            <p>We received a request to reset the password for your account associated with <strong>${data.email}</strong>.</p>
            
            <p>Click the button below to reset your password:</p>
            
            <div style="text-align: center;">
              <a href="${data.resetLink}" class="button">Reset Password</a>
            </div>
            
            <p>Or copy and paste this link into your browser:</p>
            <p><code style="word-break: break-all;">${data.resetLink}</code></p>
            
            <div class="security-notice">
              <strong>🔒 Security Notice:</strong> This link will expire ${data.expiresIn || 'in 1 hour'} for your security. 
              If you need to reset your password after this time, please request a new reset link.
            </div>
            
            ${data.ipAddress || data.userAgent ? `
              <div class="details">
                <strong>Request Details:</strong><br>
                ${data.ipAddress ? `IP Address: ${data.ipAddress}<br>` : ''}
                ${data.userAgent ? `Device: ${data.userAgent}` : ''}
              </div>
            ` : ''}
            
            <p><strong>Didn't request this?</strong></p>
            <p>If you didn't request a password reset, please ignore this email. Your password won't be changed unless you click the link above and create a new one.</p>
            
            <p>For security reasons, we recommend:</p>
            <ul>
              <li>Using a strong, unique password</li>
              <li>Enabling two-factor authentication if available</li>
              <li>Not sharing your password with anyone</li>
            </ul>
            
            <p>Best regards,<br>The Security Team</p>
          </div>
          <div class="footer">
            <p>This is an automated message, please do not reply to this email.</p>
            <p>© 2024 Your Company. All rights reserved.</p>
          </div>
        </body>
      </html>
    `,
    text: (data) => `
Password Reset Request

Hi${data.name ? ` ${data.name}` : ''},

We received a request to reset the password for your account associated with ${data.email}.

Reset your password: ${data.resetLink}

🔒 Security Notice: This link will expire ${data.expiresIn || 'in 1 hour'} for your security. If you need to reset your password after this time, please request a new reset link.

${data.ipAddress || data.userAgent ? `Request Details:
${data.ipAddress ? `IP Address: ${data.ipAddress}` : ''}
${data.userAgent ? `Device: ${data.userAgent}` : ''}` : ''}

Didn't request this?
If you didn't request a password reset, please ignore this email. Your password won't be changed unless you click the link above and create a new one.

For security reasons, we recommend:
- Using a strong, unique password
- Enabling two-factor authentication if available
- Not sharing your password with anyone

Best regards,
The Security Team

This is an automated message, please do not reply to this email.
© 2024 Your Company. All rights reserved.
    `.trim(),
  },
};

export function ResetPasswordEmail(props: ResetPasswordEmailData) {
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: 600, margin: '0 auto' }}>
      <div style={{
        backgroundColor: '#dc3545',
        color: 'white',
        padding: 30,
        textAlign: 'center',
        borderRadius: '8px 8px 0 0',
      }}>
        <h1>Password Reset Request</h1>
      </div>
      <div style={{
        backgroundColor: '#ffffff',
        padding: '40px 30px',
        border: '1px solid #e2e8f0',
        borderRadius: '0 0 8px 8px',
      }}>
        <p>Hi{props.name ? ` ${props.name}` : ''},</p>
        
        <p>We received a request to reset the password for your account associated with <strong>{props.email}</strong>.</p>
        
        <p>Click the button below to reset your password:</p>
        
        <div style={{ textAlign: 'center' }}>
          <a 
            href={props.resetLink} 
            style={{
              display: 'inline-block',
              padding: '14px 28px',
              backgroundColor: '#dc3545',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 6,
              fontWeight: 500,
              margin: '20px 0',
            }}
          >
            Reset Password
          </a>
        </div>
        
        <p>Or copy and paste this link into your browser:</p>
        <p>
          <code style={{
            backgroundColor: '#f7fafc',
            padding: '2px 6px',
            borderRadius: 3,
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}>
            {props.resetLink}
          </code>
        </p>
        
        <div style={{
          backgroundColor: '#f0f9ff',
          borderLeft: '4px solid #3182ce',
          padding: 12,
          margin: '20px 0',
          fontSize: 14,
        }}>
          <strong>🔒 Security Notice:</strong> This link will expire {props.expiresIn || 'in 1 hour'} for your security. 
          If you need to reset your password after this time, please request a new reset link.
        </div>
        
        {(props.ipAddress || props.userAgent) && (
          <div style={{
            backgroundColor: '#f7fafc',
            padding: 15,
            borderRadius: 6,
            margin: '20px 0',
            fontSize: 14,
          }}>
            <strong>Request Details:</strong><br />
            {props.ipAddress && <>IP Address: {props.ipAddress}<br /></>}
            {props.userAgent && <>Device: {props.userAgent}</>}
          </div>
        )}
        
        <p><strong>Didn't request this?</strong></p>
        <p>If you didn't request a password reset, please ignore this email. Your password won't be changed unless you click the link above and create a new one.</p>
        
        <p>For security reasons, we recommend:</p>
        <ul>
          <li>Using a strong, unique password</li>
          <li>Enabling two-factor authentication if available</li>
          <li>Not sharing your password with anyone</li>
        </ul>
        
        <p>Best regards,<br />The Security Team</p>
      </div>
      <div style={{ textAlign: 'center', marginTop: 40, color: '#718096', fontSize: 14 }}>
        <p>This is an automated message, please do not reply to this email.</p>
        <p>© 2024 Your Company. All rights reserved.</p>
      </div>
    </div>
  );
}